import React, {useMemo, useState} from 'react';
import {Link, Route, Switch, useLocation, useSearchParams} from "wouter";
import useSWR from "swr";
import {swrFetcher} from "@/utils/api.ts";
import {useDocumentTitle} from "usehooks-ts";
import {FiEdit, FiEye} from "react-icons/fi";
import RenderPostType from "../../components/ui/RenderPostType.tsx";
import {AssetResponseItem, GenericAsset, TweetAsset} from "@/@types/AssetTypes.ts";
import RenderPostCaption from "../../components/ui/RenderPostCaption.tsx";
import FacebookPostPreview from "../../components/previews/FacebookPostPreview.tsx";
import TwitterThreadPreview from "@/components/previews/TwitterThreadPreview.tsx";
import dayjs from "dayjs";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {FaFacebook, FaXTwitter} from "react-icons/fa6";
import RenderAssetsAsCollage from "@/components/ui/RenderAssetsAsCollage.tsx";
import RangePicker from "@/components/RangePicker.tsx";
import Input from "@/components/form/Input.tsx";
import Select from "@/components/form/Select.tsx";
import {useQueryState} from "nuqs";
import useDebouncedQueryState from "@/components/hooks/useDebouncedQueryState.ts";
import {Trans, useLingui} from "@lingui/react/macro";
import InstagramPostPreview from "@/components/previews/InstagramPostPreview.tsx";

function PostsHome() {
    const { t } = useLingui();
    useDocumentTitle(t`Posts`)
    return (
        <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="col-md-6">
                    <h2 className={"text-3xl mb-1"}>
                        <Trans>Posts</Trans>
                    </h2>
                    <p><Trans>Create posts for publishing and scheduling</Trans></p>
                </div>
                <div className="col-md-6">
                    <Link className={"btn btn-primary"} href={'/posts/create'}>
                        <Trans>New Post</Trans></Link>
                </div>
            </div>
            <div className="mb-3">
                <div role="tablist" className="tabs tabs-border">
                    <TabLink href={"/posts"} title={<Trans>Drafts</Trans>}/>
                    <TabLink href={"/posts/upcoming"} title={<Trans>Upcoming</Trans>}/>
                    <TabLink href={"/posts/published"} title={<Trans>Published</Trans>}/>
                </div>
            </div>

            <Switch>
                <Route path={"/posts"} component={SavedPosts}/>
                <Route path={"/posts/upcoming"} component={UpcomingPosts}/>
                <Route path={"/posts/published"} component={RecentPosts} />
            </Switch>
        </div>
    );
}
function TabLink({href, title}){
    const [location] = useLocation();
    return <Link href={href} role="tab" className={`tab ${location === href ? 'tab-active' : ''}`}>{title}</Link>
}

export function RenderPost({post}:{post: AssetResponseItem}){
    const {type, data, account} = post;
    if(type === "twitter-post"){
        return  <TwitterThreadPreview
            account={account}
            post_at={post.post_at}
            post={data as TweetAsset} />
    }else if(type === "facebook-page-post"){
        return <FacebookPostPreview
            account={account}
            post_at={post.post_at}
            post={(data as GenericAsset).post} />
    }else if(type === "instagram-post") {
        return <InstagramPostPreview
            account={account}
            post_at={post.post_at}
            post={(data as GenericAsset).post} />
    } else return <RenderGenericPost post={data as GenericAsset} />
}

function RenderGenericPost({post}:{
    post?: GenericAsset
}){
    const {assets, caption} = post?.post || {}
    return <div>
        <div className="p-3">
            <RenderPostCaption caption={caption} />
        </div>
        {
            assets?.length > 0 ? <RenderAssetsAsCollage assets={assets} />
            : null
        }
    </div>
}


export function RenderPostItem({
                            post,
                            // mutate,
                        }:{
    post: AssetResponseItem,
    // mutate: ()=>void,
}) {
    const {
        // data,
        type, title,status,
        // account, post_at,

        id,metadata} = post;
    const error_message = metadata?.data?.error?.message || ''
    return <div
        className={'card bg-base-100 shadow border border-base-200'}>
        <div
            className={"overflow-hidden"}
            style={{
                height:300,
                overflow:'hidden',
                display:'block',
                maskImage: "linear-gradient(to bottom, rgba(0, 0, 0, 1) 240px, rgba(0, 0, 0, 0) 300px)"
            }}>
        {
            status === "error" && error_message ? <div className="alert alert-danger">{error_message}</div> : null
        }
            <div>
                {
                    title ?
                    <h5 className={"px-3 pt-2 font-semibold text-lg"}>{title}</h5> : null
                }
                <div className="p-2">
                    <div className="space-between gap-2">
                        <RenderPostType type={type}/>
                        {
                            status === "published" ? <React.Fragment>
                                <Link href={"/posts/published/" + id}
                                      className={"btn btn-ghost inline-flex items-center gap-2"}>
                                    <FiEye/>&nbsp;<Trans>View Post</Trans></Link>
                            </React.Fragment> : null
                        }
                        {
                            status === "draft" || status === 'pending' || status === "error" ? <React.Fragment>
                                <Link to={"/posts/edit/" + id}
                                      className={"btn btn-ghost inline-flex items-center gap-2"}>
                                    <FiEdit/>&nbsp;<Trans>Edit</Trans></Link>
                            </React.Fragment> : null
                        }
                    </div>
                </div>
                <RenderPost post={post}/>
            </div>
        </div>
    </div>
}


function SavedPosts() {
    const str = useFilterStringFromParams();
    const {data, isLoading,
        // mutate,
    } = useSWR<{
        assets: AssetResponseItem[]
    }>(`/assets?status=draft&${str}`, swrFetcher)

    return <div>
        <div className="mb-2">
            <FilterBar />
        </div>
        {
            isLoading ? <RenderLoadingScreen/> : <div>
                {
                    data?.assets?.length === 0 ?
                        <p>
                            <Trans>
                            No saved drafts here. Start by <Link to={'/posts/create'}>creating one here</Link>
                            </Trans>
                        </p> :
                        <ul className={"grid grid-cols-1 md:grid-cols-2 gap-2"}>
                            {
                                data?.assets?.map((item) => {
                                    return <RenderPostItem
                                        // mutate={mutate}
                                        post={item}
                                        key={String(item.id)}/>
                                })
                            }
                        </ul>
                }
            </div>
        }

    </div>
}

function RecentPosts() {
    const str = useFilterStringFromParams();
    const {data, isLoading} = useSWR<{
        assets: AssetResponseItem[]
    }>(`/assets?status=published,error&${str}`, swrFetcher);

    return (
        <>
            <div className="mb-2">
                <FilterBar />
            </div>
        {
                isLoading ? <RenderLoadingScreen /> : <div>
            {
                data?.assets?.length === 0 ? <p>No recent posts</p> :
                    <ul className={"grid grid-cols-1 md:grid-cols-2 gap-2"}>
                    {
                        data.assets.map((item) => {
                            return <RenderPostItem
                                    // mutate={mutate}
                                    post={item}
                                    key={String(item.id)} />
                        })
                    }
                    </ul>
                }
                </div>
            }
        </>
    )
}

const post_types_map = {
    'twitter':'twitter-post',
    'facebook':'facebook-page-post,facebook-page-reel,facebook-page-story',
    'instagram':'instagram-post,instagram-reel,instagram-story',
}

function getMultiplierCeil(value, multiplier) {
    return Math.ceil(value / multiplier) * multiplier;
}

const Calendar = ({events}) => {
    const [currentDate, setCurrentDate] = useState(dayjs());

    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");
    const startDay = startOfMonth.day();
    const endDay = getMultiplierCeil(currentDate.daysInMonth() + startDay, 7) - currentDate.daysInMonth() - startDay
    const daysInMonth = endOfMonth.date();
    const prevMonth = () => setCurrentDate(currentDate.subtract(1, "month"));
    const nextMonth = () => setCurrentDate(currentDate.add(1, "month"));
    const days = Array.from({ length: daysInMonth }, (_, i) => startOfMonth.add(i, "day"));
    const height = 500;
    const columnHeight = height / 6;
    return (
        <div className="bg-white shadow-lg rounded-lg">
            <div className="flex justify-between items-center mb-4">
                <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-200">
                    <ChevronLeft size={20} />
                </button>
                <h2 className="text-lg font-semibold">
                    {currentDate.format("MMMM YYYY")}
                </h2>
                <button onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-200">
                    <ChevronRight size={20} />
                </button>
            </div>
            <div className="grid divide-x divide-y border divide-base-200 border-base-200 grid-cols-7">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="text-gray-500 font-medium text-center">
                        {day}
                    </div>
                ))}
                {Array(startDay).fill(null).map((_, i) => (
                    <div
                        key={i}
                        className={`p-2 bg-base-200 text-end`} style={{height:columnHeight}}>
                        {startOfMonth.subtract(startDay - i, "day").date()}
                    </div>
                ))}
                {days.map((day) => {
                    const d = day
                    const eventsForThisDate = events.filter((event) => dayjs(event.start).isSame(d, "day"));
                    const hours = [...new Set(eventsForThisDate.map((event) => dayjs(event.start).hour()))].sort().reverse()
                    const isSame = day.isSame(dayjs(), "day")
                    return <div
                        key={day.date()}
                        style={{height: columnHeight}}
                        className={`relative`}>
                        {isSame ? <div className={"h-1 bg-primary absolute top-0 left-0 right-0"}></div> : null}
                        <div className={"p-2 text-end"}>
                            {day.date()}
                        </div>
                        <div className="absolute p-2 inset-0">
                            {
                                hours.map((i)=> {
                                    const eventsThisHour = eventsForThisDate.filter((event) => dayjs(event.start).hour() === i)
                                        .sort((a, b)=>dayjs(a.start).minute() - dayjs(b.start).minute())
                                    return <div key={String(i)} className="flex mb-1 gap-2">
                                        {eventsThisHour.map((event) => {
                                            if (event.type === "twitter-post") {
                                                return <div title={dayjs(event.start).format('HH:mm')} key={event.title}>
                                                    <FaXTwitter/>
                                                </div>
                                            } else {
                                                return <div title={dayjs(event.start).format('HH:mm')}  key={event.title}>
                                                    <FaFacebook/>
                                                </div>
                                            }
                                        })}
                                    </div>
                                })
                            }
                        </div>
                    </div>
                })}
                {Array(endDay).fill(null).map((_, i) => (
                    <div
                        key={i}
                        className={`p-2 bg-base-200  text-end`} style={{height: columnHeight}}>
                        {endOfMonth.add(i + 1, "day").date()}
                    </div>
                ))}
            </div>
        </div>
    );
};

function RenderLoadingScreen() {
    return <div className="skeleton">
        <div className="card h-54">
            <div className="card-body"></div>
        </div>
    </div>
}

const post_types = Object.values(post_types_map).join(",")

function FilterBar(){
    const [type, setType] = useQueryState('type', {
        defaultValue:post_types
    });
    const [search, setSearch] = useDebouncedQueryState('search', 300);
    const [dates, setDates] = useQueryState('dates',{
        defaultValue:''
    });
    const [d1, d2] = dates ? dates.split("|").map((d)=>dayjs(d).format('YYYY-MM-DD')) : []

    return  <div className="grid md:gap-6 grid-cols-1 gap-2 md:grid-cols-3">
        <RangePicker
            placeholder={"Filter by dates"}
            onChange={(dates)=>{
            if(dates) {
                setDates(dates.join("|"))
            }else {
                setDates('')
            }
        }} value={[d1, d2]} />
        <Input
            placeholder={"Search..."}
            type={"search"} value={search} onChange={setSearch} />
        <Select options={[
            {
                label:"All",
                value:""
            },
            {
                label:"Twitter",
                value:"twitter-post"
            },
            {
                label:"Facebook",
                value:"facebook-page-post"
            },
            {
                label:"Instagram",
                value:"instagram-post,instagram-reel,instagram-story"
            }
        ]} value={type} onChange={setType} />
    </div>
}

function useFilterStringFromParams(){
    const [searchParams] = useSearchParams()
    const dates = searchParams.get('dates');
    const search = searchParams.get('search');
    const type = searchParams.get('type');
    return useMemo(()=>{
        const u = new URLSearchParams();
        if(dates){
            const [d1, d2] = dates.split("|")
            u.set('start', d1)
            u.set('end', d2)
        }
        if(search){
            u.set('search', search)
        }
        if(type){
            u.set('type', type)
        }
        return u.toString()
    },[search, dates, type])
}

function UpcomingPosts() {
    const str =  useFilterStringFromParams();
    const {data, isLoading} = useSWR<{
        assets: AssetResponseItem[]
    }>(`/assets?status=pending&${str}`, swrFetcher,
        // {
        // refreshInterval: 3000
        // }
    );

    return (
        <div>

             <Calendar events={data?.assets.map((i)=>{
                 const titleFallback =
                     i.type === "twitter-post" ? (i.data as TweetAsset).post[0].caption :
                         (i.data as GenericAsset).post.caption;

                 return {
                     type:i.type,
                     title: i.title || titleFallback,
                     start: new Date(i.post_at),
                     color: i.type === "twitter-post" ? '#000': '#1877f2',
                     end: dayjs(i.post_at).add(10, 'minutes').toDate(),
                 }
             }) || []} />
            <div className="my-2">
                <FilterBar />
            </div>
            {
                isLoading ? <RenderLoadingScreen /> : <div>
                {
                    data?.assets?.length === 0 ? <p>No posts scheduled for publishing. <Link to={"/posts/create"}>Create one here</Link></p> :
                        <ul className={"grid grid-cols-1 md:grid-cols-2 gap-2"}>
                        {
                            data?.assets?.map((item)=>{
                                return <RenderPostItem
                                    // mutate={mutate}
                                    key={String(item.id)}
                                    post={item}
                                />
                            })
                        }
                    </ul>
                }
                </div>
            }
        </div>
    )

}


export default PostsHome;
